/**
 * Backend Realtime API WebSocket 클라이언트
 *
 * OpenAI Realtime API + RAG + 인터넷 검색 통합
 */

import type {
  ClientMessage,
  ServerMessage,
  ConnectionState,
  SearchState,
  RagContextMessage
} from '../types/messages';
import { getRealtimeWsUrl } from '../const/endpoint';

export type MessageHandler = (message: ServerMessage) => void;

export interface BackendRealtimeConfig {
  agentId: string;
  avatarId: string;
  sessionId: string;
  enableWebSearch?: boolean;
  token?: string;
  provider?: 'openai' | 'google' | 'none';  // LLM provider 선택
  maxReconnectAttempts?: number;
  pingIntervalMs?: number;
  connectionTimeoutMs?: number;
  onMessage?: MessageHandler;
  onConnectionChange?: (state: ConnectionState) => void;
  onSearchStateChange?: (state: SearchState, query?: string) => void;
  onTextDelta?: (delta: string, fullText: string) => void;
  onRagContext?: (contextUsed: RagContextMessage['context_used']) => void;
  onError?: (error: Error) => void;
}

export class BackendRealtimeAPI {
  private ws: WebSocket | null = null;
  private config: BackendRealtimeConfig;
  private connectionState: ConnectionState = 'disconnected';
  private searchState: SearchState = 'idle';
  private currentResponseText: string = '';
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number;
  private pingIntervalMs: number;
  private connectionTimeoutMs: number;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;

  constructor(config: BackendRealtimeConfig) {
    this.config = config;
    this.maxReconnectAttempts = config.maxReconnectAttempts ?? 3;
    this.pingIntervalMs = config.pingIntervalMs ?? 30000;
    this.connectionTimeoutMs = config.connectionTimeoutMs ?? 10000;
  }

  /**
   * WebSocket 연결
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[BackendRealtime] Already connected');
      return;
    }

    this.setConnectionState('connecting');

    const url = getRealtimeWsUrl(
      this.config.agentId,
      this.config.avatarId,
      this.config.sessionId,
      this.config.enableWebSearch ?? false,
      this.config.token,
      this.config.provider
    );

    console.log('[BackendRealtime] Connecting to:', url);

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          console.log('[BackendRealtime] Connected');
          this.setConnectionState('connected');
          this.reconnectAttempts = 0;
          this.startPing();
          resolve();
        };

        this.ws.onmessage = (event) => {
          let message: ServerMessage;
          try {
            message = JSON.parse(event.data);
          } catch (e) {
            console.error('[BackendRealtime] Failed to parse message:', e);
            return;
          }
          try {
            this.handleMessage(message);
          } catch (e) {
            console.error('[BackendRealtime] Error handling message:', message.type, e);
            this.config.onError?.(e instanceof Error ? e : new Error(String(e)));
          }
        };

        this.ws.onerror = (error) => {
          console.error('[BackendRealtime] WebSocket error:', error);
          this.setConnectionState('error');
          this.config.onError?.(new Error('WebSocket connection error'));
        };

        this.ws.onclose = (event) => {
          console.log('[BackendRealtime] Disconnected:', event.code, event.reason);
          this.stopPing();

          // 인증 실패(4001)는 재연결하지 않음
          if (event.code === 4001 || event.code === 4002) {
            this.setConnectionState('error');
            this.config.onError?.(new Error(`인증 실패 (${event.code}): ${event.reason || '토큰을 확인해주세요'}`));
            return;
          }

          if (this.connectionState !== 'disconnected') {
            this.attemptReconnect();
          }
        };

        // 연결 타임아웃
        const timeoutId = setTimeout(() => {
          if (this.connectionState === 'connecting') {
            this.ws?.close();
            reject(new Error('Connection timeout'));
          }
        }, this.connectionTimeoutMs);

        // 연결 성공 시 타임아웃 정리를 위해 원래 onopen을 래핑
        const originalOnOpen = this.ws.onopen;
        this.ws.onopen = (ev) => {
          clearTimeout(timeoutId);
          if (originalOnOpen && this.ws) originalOnOpen.call(this.ws, ev);
        };

      } catch (error) {
        this.setConnectionState('error');
        reject(error);
      }
    });
  }

  /**
   * 연결 해제
   */
  disconnect(): void {
    this.setConnectionState('disconnected');
    this.stopPing();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * 텍스트 메시지 전송
   */
  sendText(content: string): boolean {
    if (!this.isConnected()) {
      console.warn('[BackendRealtime] Not connected');
      return false;
    }

    this.currentResponseText = '';

    const message: ClientMessage = { type: 'text', content };
    this.ws!.send(JSON.stringify(message));
    console.log('[BackendRealtime] Sent text:', content.substring(0, 50));
    return true;
  }

  /**
   * RAG 컨텍스트 포함 텍스트 메시지 전송
   */
  sendTextWithRag(content: string): boolean {
    if (!this.isConnected()) {
      console.warn('[BackendRealtime] Not connected');
      return false;
    }

    this.currentResponseText = '';

    const message: ClientMessage = { type: 'text_with_rag', content };
    this.ws!.send(JSON.stringify(message));
    console.log('[BackendRealtime] Sent text with RAG:', content.substring(0, 50));
    return true;
  }

  /**
   * 웹 검색 모드 설정
   */
  setWebSearchMode(enabled: boolean): boolean {
    if (!this.isConnected()) {
      console.warn('[BackendRealtime] Not connected');
      return false;
    }

    const message: ClientMessage = { type: 'set_web_search', enabled };
    this.ws!.send(JSON.stringify(message));
    console.log('[BackendRealtime] Set web search mode:', enabled);
    return true;
  }

  /**
   * 연결 상태 확인
   */
  isConnected(): boolean {
    return this.connectionState === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * 현재 연결 상태
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * 현재 검색 상태
   */
  getSearchState(): SearchState {
    return this.searchState;
  }

  // =========================================================================
  // Private Methods
  // =========================================================================

  private handleMessage(message: ServerMessage): void {
    // 공통 콜백 호출
    this.config.onMessage?.(message);

    switch (message.type) {
      case 'session.ready':
        console.log('[BackendRealtime] Session ready:', message);
        break;

      case 'response.text.delta':
        this.currentResponseText += message.delta;
        this.config.onTextDelta?.(message.delta as string, this.currentResponseText);
        break;

      case 'response.done':
        console.log('[BackendRealtime] Response done');
        // 검색 상태 리셋은 use-search-chat.ts의 setTimeout에서 처리
        break;

      case 'search.started':
        console.log('[BackendRealtime] Search started:', message.query);
        this.setSearchState('searching', message.query as string | undefined);
        break;

      case 'search.completed':
        console.log('[BackendRealtime] Search completed:', message.results_count, 'results');
        this.setSearchState('completed', message.query as string | undefined);
        break;

      case 'rag.context':
        console.log('[BackendRealtime] RAG context received:', (message as RagContextMessage).context_used?.count, 'documents');
        this.config.onRagContext?.((message as RagContextMessage).context_used);
        break;

      case 'web_search.updated':
        console.log('[BackendRealtime] Web search mode updated:', message.enabled);
        break;

      case 'error':
        console.error('[BackendRealtime] Error:', message.message);
        this.config.onError?.(new Error(message.message as string));
        break;

      case 'pong':
        // Ping/Pong 응답 무시
        break;

      default:
        // 기타 OpenAI 메시지는 그대로 전달
        break;
    }
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.config.onConnectionChange?.(state);
  }

  private setSearchState(state: SearchState, query?: string): void {
    this.searchState = state;
    this.config.onSearchStateChange?.(state, query);
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[BackendRealtime] Max reconnect attempts reached');
      this.setConnectionState('error');
      this.config.onError?.(new Error('재연결 실패: 페이지를 새로고침해주세요.'));
      return;
    }

    this.reconnectAttempts++;
    this.setConnectionState('reconnecting');

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    console.log(`[BackendRealtime] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect().catch((error) => {
        console.error('[BackendRealtime] Reconnect failed:', error);
      });
    }, delay);
  }

  private startPing(): void {
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        this.ws!.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.pingIntervalMs);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}
